import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  ConfirmContext,
  registerConfirmHost,
  type ConfirmFunction,
  type ConfirmRequest,
  type DestructiveConfirmRequest,
  type TerminalConfirmRequest,
} from './ConfirmContext';
import { ConfirmDialog } from './ConfirmDialog';

type Outcome = boolean | string | null;

/** Only ever needs to be unique, to key one dialog apart from the next. */
let sequence = 0;

/**
 * How long a question that has just come up from the queue ignores answers.
 *
 * The browser hands the second click of a double-click to whatever is under the pointer by then,
 * and the next question's button sits exactly where the last one's did. Half a second covers a
 * double-click at the system default and is far too short for anyone to have read the question.
 */
const PROMOTED_GUARD_MS = 500;

interface Pending {
  readonly id: number;
  readonly request: ConfirmRequest;
  readonly resolve: (outcome: Outcome) => void;
}

function createAsk(enqueue: (request: ConfirmRequest) => Promise<Outcome>): ConfirmFunction {
  function ask(request: DestructiveConfirmRequest): Promise<boolean>;
  function ask(request: TerminalConfirmRequest): Promise<string | null>;
  function ask(request: ConfirmRequest): Promise<Outcome> {
    return enqueue(request);
  }
  return ask;
}

function declined(request: ConfirmRequest): Outcome {
  return request.tier === 'terminal' ? null : false;
}

/**
 * Hosts the confirmation dialog that {@link useConfirm} opens.
 *
 * One dialog at a time: a second request made while one is showing waits its
 * turn rather than stacking a second modal on the first. The dialog is rendered
 * here, beside the application, so a confirmation raised from inside another
 * dialog sits above it and takes focus back to it when it closes.
 *
 * Each question is answered once, and the first answer wins. A question that
 * was waiting behind another ignores answers for half a second after it appears,
 * so one double-click, or a key held down, cannot answer the question before it
 * and then the question after it without anyone having read it.
 *
 * @param children the application
 * @returns the provider and, while one is pending, the dialog
 */
export function ConfirmProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [queue, setQueue] = useState<readonly Pending[]>([]);
  const waiting = useRef<readonly Pending[]>([]);
  const answered = useRef(new WeakSet<Pending>());
  const promoted = useRef<{ readonly id: number; readonly at: number } | null>(null);

  const enqueue = useCallback(
    (request: ConfirmRequest) =>
      new Promise<Outcome>((resolve) => {
        sequence += 1;
        const pending: Pending = { id: sequence, request, resolve };
        setQueue((current) => [...current, pending]);
      }),
    [],
  );
  const ask = useMemo(() => createAsk(enqueue), [enqueue]);

  const settle = useCallback((pending: Pending, outcome: Outcome) => {
    const mark = promoted.current;
    const justPromoted =
      mark !== null && mark.id === pending.id && performance.now() - mark.at < PROMOTED_GUARD_MS;
    if (justPromoted || answered.current.has(pending)) {
      return;
    }
    answered.current.add(pending);
    const next = waiting.current.find(
      (candidate) => candidate !== pending && !answered.current.has(candidate),
    );
    promoted.current = next === undefined ? null : { id: next.id, at: performance.now() };
    setQueue((current) => current.filter((candidate) => candidate !== pending));
    pending.resolve(outcome);
  }, []);

  useEffect(() => registerConfirmHost(ask), [ask]);

  useEffect(() => {
    waiting.current = queue;
  }, [queue]);

  // A caller that is still awaiting an answer when the provider goes away would wait forever.
  useEffect(
    () => () => {
      for (const pending of waiting.current) {
        pending.resolve(declined(pending.request));
      }
    },
    [],
  );

  const active = queue[0];

  return (
    <ConfirmContext value={ask}>
      {children}
      {active === undefined ? null : (
        <ActiveConfirmation key={active.id} pending={active} onSettle={settle} />
      )}
    </ConfirmContext>
  );
}

function ActiveConfirmation({
  pending,
  onSettle,
}: {
  pending: Pending;
  onSettle: (pending: Pending, outcome: Outcome) => void;
}): React.JSX.Element {
  const { request } = pending;
  const cancel = (): void => {
    onSettle(pending, declined(request));
  };

  if (request.tier === 'terminal') {
    return (
      <ConfirmDialog
        {...request}
        onCancel={cancel}
        onConfirm={(reason) => {
          onSettle(pending, reason);
        }}
      />
    );
  }
  return (
    <ConfirmDialog
      {...request}
      onCancel={cancel}
      onConfirm={() => {
        onSettle(pending, true);
      }}
    />
  );
}
